import {
  PDA1010_R01Card,
  PDA1010_R01Dto,
  PDA1010_R01MeasurementType,
} from "./PDA1010_R01Dto";

interface PDA1010_R01SensorGridProps {
  growRoomList: PDA1010_R01Card[];
  packingList: PDA1010_R01Card[];
  waterList: PDA1010_R01Card[];
  coldStorageList: PDA1010_R01Card[];
  isLoading: boolean;
  onSensorClick: (cardCode: string) => void;
}

interface SensorCardProps {
  card: PDA1010_R01Card;
  onSensorClick: (cardCode: string) => void;
}

/** 측정 항목에 대응하는 화면 표시 단위를 반환합니다. */
const getPDA1010_R01Unit = (
  measurementType: PDA1010_R01MeasurementType
) => (measurementType === "온도" ? "℃" : "%");

/** PLC 원본값에 decimalPlaces를 적용해 카드 표시 문자열로 변환합니다. */
const getPDA1010_R01Value = (measurement: PDA1010_R01Dto) => {
  // 최종값 문자열(lastValueStr, 6차 요청으로 옛 value 대체)이 없으면 "-" 표시
  if (measurement.lastValueStr === null || measurement.lastValueStr.trim() === "") {
    return "-";
  }

  const rawValue = Number(measurement.lastValueStr);
  if (!Number.isFinite(rawValue)) {
    return "-";
  }

  const value = rawValue / Math.pow(10, measurement.decimalPlaces);
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: measurement.decimalPlaces,
    maximumFractionDigits: measurement.decimalPlaces,
  })} ${getPDA1010_R01Unit(measurement.measurementType)}`;
};

/** 카드 제목과 카드에 포함된 측정값을 표시합니다. */
const SensorCard = ({ card, onSensorClick }: SensorCardProps) => (
  <button
    type="button"
    className="w-full border border-gray-300 bg-white text-black overflow-hidden active:border-red-500 active:ring-1 active:ring-red-500"
    onClick={() => onSensorClick(card.cardCode)}
  >
    <div className="h-[36px] flex items-center justify-center bg-gray-200 border-b border-gray-300 font-semibold text-[15px]">
      {card.cardName}
    </div>
    <div className="min-h-[52px] flex items-stretch">
      {card.measurementList.map((measurement, index) => (
        <div
          key={`${measurement.tagId}-${measurement.measurementType}`}
          className={`flex-1 flex items-center justify-center px-1 text-[16px] font-semibold ${
            index > 0 ? "border-l border-gray-300" : ""
          }`}
        >
          {getPDA1010_R01Value(measurement)}
        </div>
      ))}
    </div>
  </button>
);

interface SensorGroupProps {
  cardList: PDA1010_R01Card[];
  columnCount: 2 | 3;
  onSensorClick: (cardCode: string) => void;
}

/** 동일한 groupName의 카드를 지정한 열 개수로 배치합니다. */
const SensorGroup = ({
  cardList,
  columnCount,
  onSensorClick,
}: SensorGroupProps) => {
  if (cardList.length === 0) {
    return null;
  }

  return (
    <div
      className={`grid gap-2 mt-2 first:mt-0 ${
        columnCount === 3 ? "grid-cols-3" : "grid-cols-2"
      }`}
    >
      {cardList.map((card) => (
        <SensorCard
          key={card.cardCode}
          card={card}
          onSensorClick={onSensorClick}
        />
      ))}
    </div>
  );
};

/** 영역별로 분류된 카드 목록을 순서대로 표시하는 최신 상태 화면입니다. */
const PDA1010_R01SensorGrid = ({
  growRoomList,
  packingList,
  waterList,
  coldStorageList,
  isLoading,
  onSensorClick,
}: PDA1010_R01SensorGridProps) => (
  <div className="flex-1 min-h-0 overflow-y-auto bg-white p-3">
    {isLoading &&
    growRoomList.length === 0 &&
    packingList.length === 0 &&
    waterList.length === 0 &&
    coldStorageList.length === 0 ? (
      <div className="h-full flex items-center justify-center text-gray-500">
        조회 중입니다.
      </div>
    ) : (
      <>
        <SensorGroup
          cardList={growRoomList}
          columnCount={2}
          onSensorClick={onSensorClick}
        />
        <SensorGroup
          cardList={packingList}
          columnCount={2}
          onSensorClick={onSensorClick}
        />
        <SensorGroup
          cardList={waterList}
          columnCount={3}
          onSensorClick={onSensorClick}
        />
        <SensorGroup
          cardList={coldStorageList}
          columnCount={2}
          onSensorClick={onSensorClick}
        />
      </>
    )}
  </div>
);

export default PDA1010_R01SensorGrid;
